import React, {useEffect, useState} from "react";
import {Alert, Button, Container, Form, Spinner} from "react-bootstrap";
import {useSearchParams} from "react-router-dom";
import hostname from "../../../hostname";

export const Result = () => {
    const [params] = useSearchParams();
    const session = params.get("session");

    const [email, setEmail] = useState("");
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');

    const [polling, setPolling] = useState(false);
    const [countdown, setCountdown] = useState(30);

    const fetchStatus = async () => {
        try {
            const res = await fetch(hostname + `/index.php/payment/status/${session}`);
            const data = await res.json();

            if (!res.ok || !data.status) {
                throw new Error("Invalid session or missing status.");
            }

            setPaymentStatus(data.status);
            setEmail(data.email);
            setLoading(false);
        } catch {
            setPaymentStatus("failed");
            setLoading(false);
        }
    };

    // Step 1: Initial fetch
    useEffect(() => {
        if (session) {
            fetchStatus();
        }
    }, [session]);

    // Step 1b: If status is open, retry polling every 10s
    useEffect(() => {
        if (paymentStatus === "open" && !polling) {
            const interval = setInterval(() => {
                fetchStatus();
            }, 10000);

            setPolling(true);

            return () => clearInterval(interval);
        }
    }, [paymentStatus, polling]);

    // Countdown logic
    useEffect(() => {
        if (paymentStatus === "open") {
            const timer = setInterval(() => {
                setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [paymentStatus]);

    // Step 2: handle password creation
    const handleCompleteSignup = async (e) => {
        e.preventDefault();
        setError(null);

        if (password.length < 8) return setError("Password must be at least 8 characters.");
        if (password !== confirmPassword) return setError("Passwords do not match.");

        try {
            const res = await fetch(hostname + `/index.php/register`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({email, password, name, session}),
            });

            if (!res.ok) throw new Error("Signup failed");
            setSubmitted(true);
        } catch (err) {
            setError(err.message || "Something went wrong.");
        }
    };

    if (loading) {
        return (
            <Container className="text-center my-5">
                <Spinner animation="border"/>
                <p className="mt-3">Verifying payment...</p>
            </Container>
        );
    }

    return (
        <Container className="my-5 text-center" style={{maxWidth: "500px"}}>
            {paymentStatus === "success" ? (
                <>
                    <h1 className="mb-3">🎉 Payment Successful!</h1>
                    <p className="mb-4">Thank you for your purchase.</p>

                    {submitted ? (
                        <Alert variant="success">Your account has been created! You can now log in.</Alert>
                    ) : (
                        <>
                            <p>To complete your registration, please set your account password below.</p>
                            {error && <Alert variant="danger">{error}</Alert>}
                            <Form onSubmit={handleCompleteSignup}>
                                <Form.Group className="text-start mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control type="email" value={email} disabled/>
                                </Form.Group>
                                <Form.Group className="text-start mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)}
                                                  placeholder={"Give your name"} required/>
                                </Form.Group>
                                <Form.Group className="text-start mb-3">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        placeholder="Choose a password"
                                        value={password}
                                        autoComplete={"on"}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="text-start mb-4">
                                    <Form.Label>Confirm Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        placeholder="Confirm password"
                                        autoComplete={"on"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                                <Button variant="primary" className={"text-white"} type="submit">
                                    Complete Sign Up
                                </Button>
                            </Form>
                        </>
                    )}
                </>
            ) : paymentStatus === "open" ? (
                <>
                    <h1 className="text-warning mb-3">⏳ Payment In Progress</h1>
                    <p>We are still checking with the payment provider, please wait a bit...</p>
                    <p className="mt-3">You can manually re-check in {countdown} seconds.</p>
                    {countdown === 0 && (
                        <Button variant="outline-primary" onClick={() => {
                            setCountdown(60);
                            fetchStatus();
                        }}>
                            🔁 Re-check Now
                        </Button>
                    )}
                </>
            ) : (
                <>
                    <h1 className="text-danger mb-3">❌ Payment Failed</h1>
                    <p>Please try again or contact support if the issue persists.</p>
                </>
            )}
        </Container>
    );
};
